import { safe } from "@orpc/client";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import z from "zod";
import { useAppForm } from "@/components/form";
import { ComboboxCategory } from "@/components/forms/combobox-category";
import { ComboboxLanguage } from "@/components/forms/combobox-language";
import { ComboboxTags } from "@/components/forms/combobox-tags";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	Field,
	FieldContent,
	FieldDescription,
	FieldGroup,
	FieldLabel,
	FieldTitle,
} from "@/components/ui/field";
import {
	FormCard,
	FormCardContent,
	FormCardHeader,
	FormCardTitle,
} from "@/components/ui/form-card";
import { InputGroup, InputGroupAddon, InputGroupText } from "@/components/ui/input-group";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AdditionalDataType } from "@/enums/additional-data-type";
import { ChannelType } from "@/enums/channel-type";
import { useIconUpload } from "@/hooks/use-icon-upload";
import { getCdnUrl } from "@/lib/url";
import { orpc, validationErrors } from "@/orpc/client";

export const Route = createFileRoute("/_app/channels/$channelId/")({
	component: Page,
	params: z.object({
		channelId: z.coerce.number(),
	}),
	async loader({ context, params, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.channels.show.queryOptions({
				input: {
					channelId: params.channelId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();

	const { data: channel } = useSuspenseQuery(
		orpc.channels.show.queryOptions({
			input: {
				channelId: params.channelId,
			},
		}),
	);

	const updateIconMutation = useMutation(
		orpc.channels.updateIcon.mutationOptions({
			onSuccess: async (_data, _payload, _result, context) => {
				await context.client.invalidateQueries({ queryKey: orpc.channels.key() });
				await context.client.invalidateQueries({
					queryKey: orpc.channels.show.key({
						input: { channelId: params.channelId },
					}),
				});
				toast.success("Podcast icon updated successfully.");
			},
			onError: () => {
				toast.error("Failed to update the channel icon.");
			},
		}),
	);

	const uploadMutation = useMutation(
		orpc.channels.uploadIcon.mutationOptions({
			onError: () => {
				toast.error("Failed to start the icon upload.");
			},
		}),
	);

	const updateMutation = useMutation(
		orpc.channels.updateDetails.mutationOptions({
			onSuccess: async (_data, _payload, _result, context) => {
				await context.client.invalidateQueries({ queryKey: orpc.channels.key() });
				await context.client.invalidateQueries({
					queryKey: orpc.channels.show.key({
						input: { channelId: params.channelId },
					}),
				});
				toast.success("Podcast updated successfully.");
			},
			onError: () => {
				toast.error("Failed to update this podcast.");
			},
		}),
	);

	const form = useAppForm({
		defaultValues: {
			Author: channel.Author || "",
			CategoryID: channel.Categories.length > 0 ? channel.Categories[0].CategoryID : null,
			Copyright: channel.Copyright || "",
			Description: channel.Description || "",
			HomepageUrl: channel.HomepageUrl || "",
			Icon: null as File | null,
			IsExplicit: channel.IsExplicit || false,
			LanguageCode: channel.LanguageCode,
			OwnerEmail: channel.OwnerEmail || "",
			OwnerName: channel.OwnerName || "",
			Summary: channel.Summary || "",
			Tags:
				channel.AdditionalData.find(
					(data) => data.AdditionalDataID === AdditionalDataType.ChannelTags, // Assuming 1 is the ID for tags
				)?.ChannelAdditionalDataValue.split(",") || [],
			Title: channel.Title || "",
			TypeID: channel.TypeID,
		},
		canSubmitWhenInvalid: true,
		validators: {
			onSubmitAsync: async ({ value }) => {
				const { Icon: _icon, ...channelDetails } = value;
				const { error } = await safe(
					updateMutation.mutateAsync({
						...channelDetails,
						ID: channel.ID,
					}),
				);

				if (error) {
					return validationErrors(error) ?? "An error occurred while updating the podcast.";
				}
			},
		},
	});

	const {
		iconInputRef,
		iconPreviewUrl,
		iconUploadError,
		iconUploadProgress,
		isIconUploading,
		uploadIconFile,
		removeIcon,
		setIconUploadError,
		setIconUploadProgress,
	} = useIconUpload({
		selectedFile: form.state.values.Icon,
		requestUploadTarget: async (contentType) => {
			const { data: uploadData, error: uploadError } = await safe(
				uploadMutation.mutateAsync({
					ContentType: contentType,
					ID: channel.ID,
				}),
			);

			if (uploadError || !uploadData) {
				return null;
			}

			return uploadData;
		},
		persistIcon: async (iconKey) => {
			const { error: updateIconError } = await safe(
				updateIconMutation.mutateAsync({
					ID: channel.ID,
					IconKey: iconKey,
				}),
			);

			return !updateIconError;
		},
		clearSelectedFile: () => {
			form.setFieldValue("Icon", null);
		},
	});

	const currentIconUrl = iconPreviewUrl ?? getCdnUrl(channel.Icon?.ThumbnailURL);
	const isIconBusy = isIconUploading || uploadMutation.isPending || updateIconMutation.isPending;

	return (
		<form.AppForm>
			<form
				className="relative"
				id={form.formId}
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
			>
				<div className="grid grid-cols-12 gap-6 px-4 lg:px-6">
					<FormCard className="col-span-12" disabled={updateMutation.isPending || isIconBusy}>
						<FormCardHeader>
							<FormCardTitle>Podcast Info</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="Icon"
									children={(field) => (
										<field.FormItem
											id={`${field.form.formId}-channel-icon`}
											className="col-span-12"
										>
											<field.FormLabel>Podcast Icon</field.FormLabel>
											<div className="flex flex-col gap-4 rounded-lg border p-4 sm:flex-row sm:items-center">
												<Avatar className="size-20" rounded="sm">
													<AvatarImage src={currentIconUrl} alt={channel.Title ?? "Podcast icon"} />
													<AvatarFallback className="text-lg">
														{channel.Title?.slice(0, 1).toUpperCase() ?? "C"}
													</AvatarFallback>
												</Avatar>
												<div className="flex flex-1 flex-col gap-2">
													<div>
														<p className="text-sm font-medium">
															{field.state.value?.name ?? "Upload a JPG or PNG icon"}
														</p>
														<field.FormDescription>
															Recommended: 3000x3000 px square image, JPG or PNG.
														</field.FormDescription>
													</div>
													<div className="flex flex-wrap items-center gap-3">
														<Button
															type="button"
															variant="outline"
															onClick={() => iconInputRef.current?.click()}
															disabled={isIconBusy}
														>
															{isIconBusy ? "Uploading..." : "Choose icon"}
														</Button>
														{channel.IconID && (
															<Button
																type="button"
																variant="destructive"
																onClick={() => {
																	void removeIcon();
																}}
																disabled={isIconBusy}
															>
																Remove Icon
															</Button>
														)}
														<input
															ref={iconInputRef}
															id={`${field.form.formId}-channel-icon`}
															type="file"
															accept="image/jpeg,image/jpg,image/png"
															className="hidden"
															disabled={isIconBusy}
															onBlur={field.handleBlur}
															onChange={(event) => {
																const nextFile = event.target.files?.[0] ?? null;

																field.handleChange(nextFile);
																setIconUploadError(null);
																setIconUploadProgress(null);

																if (nextFile) {
																	void uploadIconFile(nextFile);
																}
															}}
														/>
														{iconUploadProgress !== null && (
															<span className="text-muted-foreground text-sm">
																{iconUploadProgress}% uploaded
															</span>
														)}
													</div>
													{iconUploadProgress !== null && (
														<div className="bg-muted h-2 overflow-hidden rounded-full">
															<div
																className="bg-primary h-full transition-[width]"
																style={{ width: `${iconUploadProgress}%` }}
															/>
														</div>
													)}
													{iconUploadError && (
														<p className="text-destructive text-sm" role="alert">
															{iconUploadError}
														</p>
													)}
												</div>
											</div>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="Title"
									children={(field) => (
										<field.FormItem className="col-span-12">
											<field.FormLabel>Title</field.FormLabel>
											<InputGroup>
												<field.FormGroupInput />
												<InputGroupAddon align="block-end">
													<InputGroupText className="tabular-nums">
														{field.state.value.length}/150 characters
													</InputGroupText>
												</InputGroupAddon>
											</InputGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="Summary"
									children={(field) => (
										<field.FormItem className="col-span-12">
											<field.FormLabel>Summary</field.FormLabel>
											<InputGroup>
												<field.FormTextEditor className="max-h-50 border-0 shadow-none" />
												<InputGroupAddon align="block-end">
													<InputGroupText className="tabular-nums">
														{field.state.value.length}/4000 characters
													</InputGroupText>
												</InputGroupAddon>
											</InputGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="Description"
									children={(field) => (
										<field.FormItem className="col-span-12">
											<field.FormLabel>Description</field.FormLabel>
											<InputGroup>
												<field.FormTextEditor className="border-0 shadow-none" />
												<InputGroupAddon align="block-end">
													<InputGroupText className="tabular-nums">
														{field.state.value.length}/4000 characters
													</InputGroupText>
												</InputGroupAddon>
											</InputGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="HomepageUrl"
									children={(field) => (
										<field.FormItem className="col-span-12">
											<field.FormLabel>Homepage URL</field.FormLabel>
											<field.FormInput type="url" />
											<field.FormError />
										</field.FormItem>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
					</FormCard>
					<FormCard className="col-span-12" disabled={updateMutation.isPending}>
						<FormCardHeader>
							<FormCardTitle>Podcast Content</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="LanguageCode"
									children={(field) => (
										<field.FormItem
											id={`${field.form.formId}-channel-language`}
											className="col-span-6"
										>
											<field.FormLabel>Language</field.FormLabel>
											<ComboboxLanguage
												id={`${field.form.formId}-channel-language`}
												value={field.state.value}
												onChange={(value) => field.handleChange(value)}
												onBlur={field.handleBlur}
												isInvalid={field.state.meta.isTouched && !field.state.meta.isValid}
											/>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="IsExplicit"
									children={(field) => (
										<field.FormItem
											id={`${field.form.formId}-channel-explicit`}
											className="col-span-6"
										>
											<field.FormLabel>Explicit Content</field.FormLabel>
											<RadioGroup
												className="flex flex-row gap-2"
												value={field.state.value}
												onValueChange={field.handleChange}
												onBlur={field.handleBlur}
											>
												<FieldLabel>
													<Field orientation="horizontal" className="h-8 py-1.5!">
														<FieldContent>
															<FieldTitle>Yes</FieldTitle>
														</FieldContent>
														<RadioGroupItem value={true} />
													</Field>
												</FieldLabel>
												<FieldLabel>
													<Field orientation="horizontal" className="h-8 py-1.5!">
														<FieldContent>
															<FieldTitle>No</FieldTitle>
														</FieldContent>
														<RadioGroupItem value={false} />
													</Field>
												</FieldLabel>
											</RadioGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="CategoryID"
									children={(field) => (
										<field.FormItem
											id={`${field.form.formId}-channel-category`}
											className="col-span-6"
										>
											<field.FormLabel>Category</field.FormLabel>
											<ComboboxCategory
												id={`${field.form.formId}-channel-category`}
												value={field.state.value}
												onChange={(value) => field.handleChange(value)}
												onBlur={field.handleBlur}
												isInvalid={field.state.meta.isTouched && !field.state.meta.isValid}
											/>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="Tags"
									children={(field) => (
										<field.FormItem id={`${field.form.formId}-channel-tags`} className="col-span-6">
											<field.FormLabel>Tags</field.FormLabel>
											<ComboboxTags
												id={`${field.form.formId}-channel-tags`}
												value={field.state.value}
												onChange={(value) => field.handleChange(value)}
												onBlur={field.handleBlur}
												isInvalid={field.state.meta.isTouched && !field.state.meta.isValid}
											/>
											<field.FormError />
										</field.FormItem>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
					</FormCard>
					<FormCard className="lg:col-span-6" disabled={updateMutation.isPending}>
						<FormCardHeader>
							<FormCardTitle>Podcast Type</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup className="col-span-12">
								<form.AppField
									name="TypeID"
									children={(field) => (
										<RadioGroup
											value={field.state.value}
											onValueChange={field.handleChange}
											onBlur={field.handleBlur}
											className="col-span-full"
										>
											<FieldLabel htmlFor="episodic-type">
												<Field orientation="horizontal">
													<FieldContent>
														<FieldTitle>Episodic</FieldTitle>
														<FieldDescription className="text-xs">
															This is the standard format displaying episodes from most recent to
															oldest. This setting is ideal for podcasts where each episode can be
															enjoyed independently.
														</FieldDescription>
													</FieldContent>
													<RadioGroupItem value={ChannelType.Episodic} id="episodic-type" />
												</Field>
											</FieldLabel>
											<FieldLabel htmlFor="serial-type">
												<Field orientation="horizontal">
													<FieldContent>
														<FieldTitle>Serial</FieldTitle>
														<FieldDescription className="text-xs">
															For shows where episodes follow a chronological story and are best
															experienced in order from the beginning, organized by seasons. This
															setting works well for narrative-driven and storytelling podcasts.
														</FieldDescription>
													</FieldContent>
													<RadioGroupItem value={ChannelType.Serial} id="serial-type" />
												</Field>
											</FieldLabel>
										</RadioGroup>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
					</FormCard>
					<FormCard className="lg:col-span-6" disabled={updateMutation.isPending}>
						<FormCardHeader>
							<FormCardTitle>Podcast Author</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="Author"
									children={(field) => (
										<field.FormItem className="xl:col-span-6">
											<field.FormLabel>Author</field.FormLabel>
											<field.FormInput maxLength={200} />
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="Copyright"
									children={(field) => (
										<field.FormItem className="xl:col-span-6">
											<field.FormLabel>Copyright</field.FormLabel>
											<field.FormInput maxLength={200} />
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="OwnerName"
									children={(field) => (
										<field.FormItem className="xl:col-span-6">
											<field.FormLabel>Owner Name</field.FormLabel>
											<field.FormInput maxLength={200} />
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="OwnerEmail"
									children={(field) => (
										<field.FormItem className="xl:col-span-6">
											<field.FormLabel>Owner Email</field.FormLabel>
											<field.FormInput maxLength={200} />
											<field.FormError />
										</field.FormItem>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
					</FormCard>
					<form.FormSaveBar />
				</div>
				<form.FormBottomBar />
			</form>
		</form.AppForm>
	);
}
